import { Alert, Anchor, Box, Group, Text } from '@mantine/core';
import { IconAlertTriangle, IconExternalLink } from '@tabler/icons-react';

import type { ArenaRun, ReportError } from '../hooks/useModelArena';
import { dateTimeLabel, measuredOn } from '../runLabels';

import { Breakdowns } from './Breakdowns';
import { Detail } from './Detail';
import { Leaderboard } from './Leaderboard';
import { readable } from './metricFormat';
import { RunActions } from './RunActions';

export function RunDetails({
  run,
  rankMetric,
  reportError,
}: {
  run: ArenaRun;
  rankMetric: string | null;
  reportError: ReportError;
}) {
  return (
    <Box px="sm" pb="sm" pt={4}>
      <Group gap="lg" mb="sm">
        <Detail label="Measured on" value={measuredOn(run)} />
        {Object.entries(run.scoring).map(([name, value]) => (
          <Detail key={name} label={readable(name)} value={String(value)} />
        ))}
        <Detail label="Triggered by" value={run.triggeredBy ?? 'unknown'} />
        {run.wandbRunUrl && (
          <Anchor href={run.wandbRunUrl} target="_blank" size="xs">
            <Group gap={4} wrap="nowrap">
              Open in W&B <IconExternalLink size={12} />
            </Group>
          </Anchor>
        )}
        <RunActions run={run} reportError={reportError} />
      </Group>

      <UnsettledNote run={run} />

      {run.failureReason && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} mb="sm">
          {run.failureReason}
        </Alert>
      )}

      {run.results.length > 0 ? (
        <>
          <Leaderboard results={run.results} rankMetric={rankMetric} />
          <Breakdowns results={run.results} />
        </>
      ) : (
        <Text size="sm" c="dimmed">
          Candidates: {run.candidates.join(', ')}
        </Text>
      )}
    </Box>
  );
}

/** What a run that has not settled is waiting on, and what stopping it would cost. */
function UnsettledNote({ run }: { run: ArenaRun }) {
  if (run.status === 'QUEUED') {
    return (
      <Text size="xs" c="dimmed" mb="sm">
        Not before {dateTimeLabel(run.notBefore)}. It starts once that time has passed and a worker
        is free — two comparisons can run at once.
      </Text>
    );
  }

  if (run.status === 'RUNNING') {
    return (
      <Text size="xs" c="dimmed" mb="sm">
        Each candidate is saved as it finishes, so cancelling keeps what has been measured.
      </Text>
    );
  }

  return null;
}
